import { Component } from '@theme/component';
import { debounce, onAnimationEnd, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/** Delay before closing the hover drawer so the pointer can reach the panel. */
const DRAWER_CLOSE_DELAY_MS = 200;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#drawerCloseTimer);
    this.#drawerCloseTimer = undefined;
    this.#unbindDrawer();
    this.#openedByPointer = false;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /** @type {boolean} */
  #drawerClosing = false;

  /** @type {number} */
  #drawerEpoch = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #drawerCloseTimer;

  /**
   * True when a coarse-pointer `pointerenter` just opened a submenu. The
   * synthetic `click` that follows that tap must not follow the parent URL;
   * a later tap on the already-open item should.
   * @type {boolean}
   */
  #openedByPointer = false;

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * @returns {HTMLDialogElement | null}
   */
  #getActiveDrawer() {
    if (this.#state.activeItem?.closest('[slot="overflow"]')) return null;

    const submenu = findSubmenu(this.#state.activeItem);
    return submenu instanceof HTMLDialogElement ? submenu : null;
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement | null} overflowItem
   * @param {boolean} isDefaultSlot
   * @returns {HTMLElement | null}
   */
  #activateItem(item, overflowItem, isDefaultSlot) {
    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      previouslyActiveOverflowItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    if (overflowItem && overflowItem !== item) {
      overflowItem.ariaExpanded = 'true';
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    return previouslyActiveItem;
  }

  /**
   * @param {HTMLElement} submenu
   * @param {HTMLElement} item
   * @param {Event} event
   */
  #prepareSubmenu(submenu, item, event) {
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    const committedItem = item;

    if (event instanceof FocusEvent) {
      this.dispatchEvent(new MegaMenuHoverEvent());
    } else {
      this.#hoverDispatchTimer = setTimeout(() => {
        this.#hoverDispatchTimer = undefined;

        if (this.#state.activeItem === committedItem) {
          this.dispatchEvent(new MegaMenuHoverEvent());
        }
      }, HOVER_COMMIT_DELAY_MS);
    }

    submenu.dataset.active = '';

    if (submenu instanceof HTMLDialogElement && !submenu.open) {
      submenu.show();
    }

    this.#cleanupMutationObserver();
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#cleanupMutationObserver();
          }
        });
      });
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    setTimeout(() => {
      this.#cleanupMutationObserver();
    }, 500);
  }

  /**
   * @returns {boolean}
   */
  #hasHoverPointer() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  /**
   * Record whether this activation came from a coarse-pointer enter, so the
   * following synthetic click can be distinguished from a later tap.
   * @param {Event} event
   */
  #markOpenGesture(event) {
    this.#openedByPointer = event.type === 'pointerenter' && !this.#hasHoverPointer();
  }

  /**
   * Whether this click is the same gesture that opened the submenu via pointerenter.
   * @returns {boolean}
   */
  #consumeOpenGesture() {
    const openedByPointer = this.#openedByPointer;
    this.#openedByPointer = false;
    return openedByPointer;
  }

  /**
   * @param {HTMLDialogElement} submenu
   * @param {HTMLDialogElement | null} [previousDrawer]
   * @param {{ pointerInitiated?: boolean }} [options]
   */
  #openDrawer(submenu, previousDrawer = null, options = {}) {
    const { pointerInitiated = false } = options;
    const drawerAlreadyOpen = this.hasAttribute('data-drawer-open');

    this.#drawerEpoch += 1;
    this.#drawerClosing = false;
    clearTimeout(this.#drawerCloseTimer);
    this.#drawerCloseTimer = undefined;

    this.setAttribute('data-drawer-open', '');
    this.#bindDrawer();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!submenu.open) {
      if (drawerAlreadyOpen) {
        submenu.show();
      } else {
        submenu.classList.add('menu-list__submenu--opening');
        submenu.show();
        onAnimationEnd(submenu, () => submenu.classList.remove('menu-list__submenu--opening'), { subtree: false });
      }
    }

    if (pointerInitiated) {
      this.#preventDrawerAutofocusRing(submenu);
    }

    if (previousDrawer && previousDrawer !== submenu) {
      this.#dismissDrawer(previousDrawer);
    }
  }

  /**
   * `dialog.show()` focuses the first descendant and browsers treat that as
   * `:focus-visible`, so hover-opening would outline the first accordion item.
   * Move focus to the dialog itself instead (`tabindex="-1"`, no panel ring).
   * @param {HTMLDialogElement} submenu
   */
  #preventDrawerAutofocusRing(submenu) {
    const focused = document.activeElement;
    if (!(focused instanceof HTMLElement)) return;
    if (!submenu.contains(focused) || focused === submenu) return;

    submenu.focus({ preventScroll: true });
  }

  /**
   * Instantly hides a drawer without playing the close animation.
   * @param {HTMLDialogElement} submenu
   */
  #dismissDrawer(submenu) {
    submenu.classList.remove('menu-list__submenu--opening', 'menu-list__submenu--closing');

    if (submenu.open) {
      submenu.close();
    }
  }

  /**
   * Closes the left-side megamenu drawer.
   */
  closeDrawer = async () => {
    const submenu = this.#getActiveDrawer();

    if (!submenu?.open || this.#drawerClosing) return;

    const epoch = ++this.#drawerEpoch;
    this.#drawerClosing = true;
    this.#unbindDrawer();
    submenu.classList.remove('menu-list__submenu--opening');
    submenu.classList.add('menu-list__submenu--closing');
    this.removeAttribute('data-drawer-open');
    await onAnimationEnd(submenu, undefined, { subtree: false });

    if (epoch !== this.#drawerEpoch) return;

    submenu.classList.remove('menu-list__submenu--closing');
    this.#dismissDrawer(submenu);
    this.#drawerClosing = false;
    this.#deactivate(this.#state.activeItem);
  };

  #scheduleDrawerClose() {
    clearTimeout(this.#drawerCloseTimer);
    this.#drawerCloseTimer = setTimeout(() => {
      this.#drawerCloseTimer = undefined;
      this.closeDrawer();
    }, DRAWER_CLOSE_DELAY_MS);
  }

  #bindDrawer() {
    document.addEventListener('keydown', this.#onDrawerKeyDown);
  }

  #unbindDrawer() {
    document.removeEventListener('keydown', this.#onDrawerKeyDown);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onDrawerKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (event.isComposing || event.defaultPrevented) return;

    event.preventDefault();
    this.closeDrawer();
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    clearTimeout(this.#drawerCloseTimer);
    this.#drawerCloseTimer = undefined;

    const isMoreTrigger = event.target.slot === 'more';
    const item = findMenuItem(event.target);
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    if (!item || item == this.#state.activeItem) {
      // Coarse pointers: swallow only the click that closes the opening tap.
      // A second tap on the already-open parent must follow its URL — the
      // accordion lists child links only, so that is the only way to reach
      // destinations like a parent collection page.
      if (item && event instanceof MouseEvent && event.type === 'click') {
        const isOpenGesture = this.#consumeOpenGesture();

        if (
          isOpenGesture &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !this.#hasHoverPointer()
        ) {
          event.preventDefault();
        }
      }

      return;
    }

    const submenu = findSubmenu(item);
    const isOverflowItem = Boolean(item.closest('[slot="overflow"]'));
    const currentDrawer = this.#getActiveDrawer();

    if (submenu instanceof HTMLDialogElement && !isOverflowItem) {
      if (event instanceof MouseEvent && event.type === 'click') {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        if (this.#hasHoverPointer()) return;

        event.preventDefault();
      }

      this.#markOpenGesture(event);
      const previouslyActiveItem = this.#activateItem(item, overflowItem, true);
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      this.#openDrawer(submenu, previousSubmenu instanceof HTMLDialogElement ? previousSubmenu : null, {
        pointerInitiated: !(event instanceof FocusEvent),
      });
      return;
    }

    if (currentDrawer?.open && !isOverflowItem && !isMoreTrigger) {
      return;
    }

    if (currentDrawer?.open) {
      this.#drawerEpoch += 1;
      this.#drawerClosing = false;
      this.#dismissDrawer(currentDrawer);
      this.removeAttribute('data-drawer-open');
      this.#unbindDrawer();
    }

    const isDefaultSlot = event.target.slot !== 'more' && !item.closest('[slot="overflow"]');

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    this.#markOpenGesture(event);
    const previouslyActiveItem = this.#activateItem(item, overflowItem, isDefaultSlot);

    let resolvedSubmenu = submenu || (isMoreTrigger ? findSubmenu(overflowItem) : null);
    resolvedSubmenu = resolvedSubmenu || (!isDefaultSlot ? this.overflowMenu : null);
    const hasSubmenu = Boolean(resolvedSubmenu);

    if (resolvedSubmenu) {
      this.#prepareSubmenu(resolvedSubmenu, item, event);
    }

    let finalHeight = resolvedSubmenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!resolvedSubmenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);

    if (menu instanceof HTMLDialogElement && !this.#state.activeItem?.closest('[slot="overflow"]')) {
      if (event.relatedTarget instanceof Node && menu.contains(event.relatedTarget)) return;

      const nav = this.querySelector('.header-menu__inner');

      if (nav?.matches(':hover')) return;

      const nextListItem =
        event.relatedTarget instanceof Element ? event.relatedTarget.closest('.menu-list__list-item') : null;
      const nextItem = nextListItem ? findMenuItem(nextListItem) : null;
      const nextSubmenu = findSubmenu(nextItem);

      if (
        nextItem &&
        nextListItem &&
        this.contains(nextListItem) &&
        nextSubmenu instanceof HTMLDialogElement &&
        !nextItem.closest('[slot="overflow"]')
      ) {
        return;
      }

      this.#scheduleDrawerClose();
      return;
    }

    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#openedByPointer = false;
    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      activeOverflowItem.ariaExpanded = 'false';
    }

    // Remove active state from submenu after animation completes
    if (submenu instanceof HTMLDialogElement && submenu.open && !submenu.matches(':modal')) {
      submenu.close();
    }

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) return /** @type {HTMLElement} */ (element);

  const nestedItem = element.querySelector('[ref="menuitem"]');
  if (nestedItem instanceof HTMLElement) return nestedItem;

  const listItem = element.closest('.menu-list__list-item');
  const siblingItem = listItem?.querySelector(':scope > [ref="menuitem"]') ?? listItem?.querySelector('[ref="menuitem"]');
  return siblingItem instanceof HTMLElement ? siblingItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
